import { useState, useRef, KeyboardEvent, useEffect } from 'react';
import { X, Mail, Lock, Phone, Eye, EyeOff, ArrowLeft, KeyRound, User, Loader2 } from 'lucide-react';
import { z } from 'zod';
import { useToast } from './Toast';
import {
  useLoginMutation,
  useRegisterMutation,
  useVerifyOtpMutation,
  useResendOtpMutation,
  useForgotPasswordMutation,
  useVerifyResetOtpMutation,
  useResetPasswordMutation,
  useGoogleAuthMutation,
  useAppSelector,
  useAppDispatch,
  selectPendingVerification,
  selectIsAuthenticated,
  authActions,
} from '@nimbazaar/store';

// Pakistani phone number validation
const pkPhoneRegex = /^(\+92|92|0)?3[0-9]{9}$/;

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

const signupSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  phone: z.string().regex(pkPhoneRegex, 'Please enter a valid Pakistani phone number (e.g., 03XX XXXXXXX)').optional().or(z.literal('')),
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

const forgotEmailSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type AuthMode = 'login' | 'signup' | 'verify-otp' | 'forgot-email' | 'forgot-otp' | 'reset-password';

export function AuthModal({ isOpen, onClose }: AuthModalProps) {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const pendingVerification = useAppSelector(selectPendingVerification);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  const [mode, setMode] = useState<AuthMode>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    password: '',
  });
  const [forgotEmail, setForgotEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [verifyEmail, setVerifyEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // RTK Query mutations
  const [login, { isLoading: isLoggingIn }] = useLoginMutation();
  const [register, { isLoading: isRegistering }] = useRegisterMutation();
  const [verifyOtp, { isLoading: isVerifying }] = useVerifyOtpMutation();
  const [resendOtp, { isLoading: isResending }] = useResendOtpMutation();
  const [forgotPassword, { isLoading: isSendingReset }] = useForgotPasswordMutation();
  const [verifyResetOtp, { isLoading: isVerifyingReset }] = useVerifyResetOtpMutation();
  const [resetPasswordMutation, { isLoading: isResettingPassword }] = useResetPasswordMutation();
  const [googleAuth, { isLoading: isGoogleLoading }] = useGoogleAuthMutation();

  // Refs for OTP inputs
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const googleButtonRef = useRef<HTMLDivElement>(null);

  // Auto-switch to verify-otp mode when registration is pending
  useEffect(() => {
    if (pendingVerification && isOpen) {
      setVerifyEmail(pendingVerification.email);
      setMode('verify-otp');
    }
  }, [pendingVerification, isOpen]);

  // Close modal when authenticated
  const welcomeShownRef = useRef(false);
  useEffect(() => {
    if (isAuthenticated && isOpen && !welcomeShownRef.current) {
      welcomeShownRef.current = true;
      onClose();
      toast.success('Welcome!', 'You have successfully signed in.');
    }
    if (!isOpen) {
      welcomeShownRef.current = false;
    }
  }, [isAuthenticated, isOpen, onClose, toast]);

  // Initialize Google Sign-In button — only initialize once
  const googleInitRef = useRef(false);
  const googleCallbackRef = useRef<((response: { credential: string }) => void) | null>(null);

  // Keep callback ref in sync so the closure always has latest googleAuth
  googleCallbackRef.current = async (response: { credential: string }) => {
    try {
      await googleAuth({ idToken: response.credential }).unwrap();
    } catch (error) {
      const apiError = error as { data?: { error?: { message?: string } } };
      toast.error('Google Sign-In Failed', apiError?.data?.error?.message || 'Could not sign in with Google.');
    }
  };

  useEffect(() => {
    if (!isOpen || (mode !== 'login' && mode !== 'signup')) return;
    if (!window.google || !googleButtonRef.current) return;

    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) return;

    // Only initialize once globally
    if (!googleInitRef.current) {
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (response: { credential: string }) => {
          googleCallbackRef.current?.(response);
        },
      });
      googleInitRef.current = true;
    }

    window.google.accounts.id.renderButton(googleButtonRef.current, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'continue_with',
      shape: 'rectangular',
      width: googleButtonRef.current.offsetWidth,
    });
  }, [isOpen, mode]);

  if (!isOpen) return null;

  const isLoading = isLoggingIn || isRegistering || isVerifying || isResending || isSendingReset || isVerifyingReset || isResettingPassword || isGoogleLoading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    dispatch(authActions.clearError());

    try {
      if (mode === 'login') {
        const validated = loginSchema.parse({ email: formData.email, password: formData.password });
        await login(validated).unwrap();
        // Auth state will be updated by the slice, modal will close via useEffect
      } else if (mode === 'signup') {
        const validated = signupSchema.parse(formData);
        const referralCode = sessionStorage.getItem('nimbazaar_referral') || undefined;
        await register({
          name: validated.name,
          email: validated.email,
          password: validated.password,
          phone: validated.phone || undefined,
          referralCode,
        }).unwrap();
        // Clear referral code after successful registration
        sessionStorage.removeItem('nimbazaar_referral');
        toast.success('Account created!', 'Please check your email for the verification code.');
        setVerifyEmail(formData.email);
        setMode('verify-otp');
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(newErrors);
      } else {
        const apiError = error as { data?: { error?: { message?: string; code?: string } } };
        const errorMessage = apiError?.data?.error?.message || 'Something went wrong. Please try again.';
        const errorCode = apiError?.data?.error?.code;

        // If email not verified, switch to OTP verification
        if (errorCode === 'EMAIL_NOT_VERIFIED') {
          setVerifyEmail(formData.email);
          setMode('verify-otp');
          toast.info('Verification Required', 'Please verify your email to continue.');
        } else {
          toast.error('Error', errorMessage);
        }
      }
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpValue = otp.join('');

    if (otpValue.length !== 6) {
      toast.error('Invalid OTP', 'Please enter all 6 digits.');
      return;
    }

    try {
      await verifyOtp({
        email: verifyEmail || pendingVerification?.email || '',
        otp: otpValue,
      }).unwrap();
      // Auth state will be updated, modal will close
      dispatch(authActions.clearPendingVerification());
    } catch (error) {
      const apiError = error as { data?: { error?: { message?: string } } };
      toast.error('Verification Failed', apiError?.data?.error?.message || 'Invalid or expired OTP.');
    }
  };

  const handleForgotEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    try {
      forgotEmailSchema.parse({ email: forgotEmail });
      await forgotPassword({ email: forgotEmail }).unwrap();
      toast.info('Code Sent', `We've sent a password reset code to ${forgotEmail}`);
      setOtp(['', '', '', '', '', '']);
      setMode('forgot-otp');
    } catch (error) {
      if (error instanceof z.ZodError) {
        setErrors({ forgotEmail: error.errors[0].message });
      } else {
        const apiError = error as { data?: { error?: { message?: string } } };
        toast.error('Error', apiError?.data?.error?.message || 'Failed to send reset email.');
      }
    }
  };

  const handleForgotOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpValue = otp.join('');

    if (otpValue.length !== 6) {
      toast.error('Invalid OTP', 'Please enter all 6 digits.');
      return;
    }

    try {
      const result = await verifyResetOtp({ email: forgotEmail, otp: otpValue }).unwrap();
      setResetToken(result.data.resetToken);
      setMode('reset-password');
    } catch (error) {
      const apiError = error as { data?: { error?: { message?: string } } };
      toast.error('Verification Failed', apiError?.data?.error?.message || 'Invalid or expired OTP.');
    }
  };

  const handleResendForgotOtp = async () => {
    if (!forgotEmail) return;

    try {
      await forgotPassword({ email: forgotEmail }).unwrap();
      toast.info('OTP Resent', `A new code has been sent to ${forgotEmail}`);
      setOtp(['', '', '', '', '', '']);
    } catch (error) {
      const apiError = error as { data?: { error?: { message?: string } } };
      toast.error('Error', apiError?.data?.error?.message || 'Failed to resend OTP.');
    }
  };

  const handleResetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (newPassword.length < 8) {
      setErrors({ newPassword: 'Password must be at least 8 characters' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrors({ confirmPassword: 'Passwords do not match' });
      return;
    }

    try {
      await resetPasswordMutation({ token: resetToken, password: newPassword }).unwrap();
      toast.success('Password Reset!', 'Your password has been reset successfully. You can now sign in.');
      setNewPassword('');
      setConfirmPassword('');
      setResetToken('');
      goBackToLogin();
    } catch (error) {
      const apiError = error as { data?: { error?: { message?: string } } };
      toast.error('Reset Failed', apiError?.data?.error?.message || 'Failed to reset password. Please try again.');
    }
  };

  const handleResendOtp = async () => {
    const email = verifyEmail || pendingVerification?.email;
    if (!email) return;

    try {
      await resendOtp({ email }).unwrap();
      toast.info('OTP Resent', `A new code has been sent to ${email}`);
      setOtp(['', '', '', '', '', '']);
    } catch (error) {
      const apiError = error as { data?: { error?: { message?: string } } };
      toast.error('Error', apiError?.data?.error?.message || 'Failed to resend OTP.');
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newOtp = [...otp];
      pastedData.split('').forEach((digit, i) => {
        if (i < 6) newOtp[i] = digit;
      });
      setOtp(newOtp);
      const lastIndex = Math.min(pastedData.length - 1, 5);
      otpRefs.current[lastIndex]?.focus();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const switchMode = () => {
    setMode(mode === 'login' ? 'signup' : 'login');
    setFormData({ name: '', phone: '', email: '', password: '' });
    setErrors({});
    dispatch(authActions.clearError());
  };

  const goToForgotPassword = () => {
    setMode('forgot-email');
    setErrors({});
  };

  const goBackToLogin = () => {
    setMode('login');
    setForgotEmail('');
    setOtp(['', '', '', '', '', '']);
    setErrors({});
    setResetToken('');
    setNewPassword('');
    setConfirmPassword('');
    dispatch(authActions.clearPendingVerification());
  };

  const getHeaderContent = () => {
    switch (mode) {
      case 'login':
        return { title: 'Welcome Back!', subtitle: 'Sign in to continue to NimBazaar' };
      case 'signup':
        return { title: 'Create Account', subtitle: 'Join NimBazaar and start shopping' };
      case 'verify-otp':
        return { title: 'Verify Email', subtitle: `Enter the code sent to ${verifyEmail || pendingVerification?.email}` };
      case 'forgot-email':
        return { title: 'Forgot Password?', subtitle: 'Enter your email to receive reset instructions' };
      case 'forgot-otp':
        return { title: 'Enter OTP', subtitle: `We sent a code to ${forgotEmail}` };
      case 'reset-password':
        return { title: 'New Password', subtitle: 'Create a new password for your account' };
    }
  };

  const headerContent = getHeaderContent();

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md sm:mx-4 overflow-hidden max-h-[92vh] sm:max-h-[90vh] overflow-y-auto scrollbar-hide safe-bottom">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Back Button */}
        {(mode === 'forgot-email' || mode === 'forgot-otp' || mode === 'verify-otp' || mode === 'reset-password') && (
          <button
            onClick={goBackToLogin}
            className="absolute top-4 left-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors z-10"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}

        {/* Header */}
        <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-4 sm:px-8 pt-6 sm:pt-8 pb-10 sm:pb-12 text-white text-center">
          <h2 className="text-2xl font-bold mb-2">{headerContent.title}</h2>
          <p className="text-primary-100 text-sm">{headerContent.subtitle}</p>
        </div>

        {/* Form Container */}
        <div className="px-4 sm:px-8 pb-6 sm:pb-8 -mt-6">
          {/* Login/Signup Form */}
          {(mode === 'login' || mode === 'signup') && (
            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-6">
              {/* Name Field (Signup only) */}
              {mode === 'signup' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter your name"
                      disabled={isLoading}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all disabled:bg-gray-50 disabled:cursor-not-allowed ${
                        errors.name ? 'border-red-500' : 'border-gray-200'
                      }`}
                    />
                  </div>
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                  )}
                </div>
              )}

              {/* Phone Field (Signup only) */}
              {mode === 'signup' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number <span className="text-gray-400 font-normal">(Optional)</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="03XX XXXXXXX"
                      disabled={isLoading}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all disabled:bg-gray-50 disabled:cursor-not-allowed ${
                        errors.phone ? 'border-red-500' : 'border-gray-200'
                      }`}
                    />
                  </div>
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
                  )}
                </div>
              )}

              {/* Email Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email"
                    disabled={isLoading}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all disabled:bg-gray-50 disabled:cursor-not-allowed ${
                      errors.email ? 'border-red-500' : 'border-gray-200'
                    }`}
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                )}
              </div>

              {/* Password Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder={mode === 'signup' ? 'Min 8 characters' : 'Enter your password'}
                    disabled={isLoading}
                    className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all disabled:bg-gray-50 disabled:cursor-not-allowed ${
                      errors.password ? 'border-red-500' : 'border-gray-200'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-500">{errors.password}</p>
                )}
              </div>

              {/* Forgot Password (Login only) */}
              {mode === 'login' && (
                <div className="text-right mb-4">
                  <button
                    type="button"
                    onClick={goToForgotPassword}
                    className="text-sm text-primary-500 hover:text-primary-600 font-medium"
                  >
                    Forgot Password?
                  </button>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-semibold py-3 rounded-lg transition-colors shadow-md hover:shadow-lg flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {mode === 'login' ? 'Signing In...' : 'Creating Account...'}
                  </>
                ) : (
                  mode === 'login' ? 'Sign In' : 'Create Account'
                )}
              </button>

              {/* Divider */}
              <div className="flex items-center my-4 sm:my-5">
                <div className="flex-1 border-t border-gray-200" />
                <span className="px-3 text-xs sm:text-sm text-gray-400">or</span>
                <div className="flex-1 border-t border-gray-200" />
              </div>

              {/* Google Sign-In Button */}
              <div
                ref={googleButtonRef}
                className="w-full flex justify-center [&>div]:!w-full"
              />

              {/* Switch Mode Link */}
              <p className="text-center text-sm text-gray-600 mt-6">
                {mode === 'login' ? (
                  <>
                    Don't have an account?{' '}
                    <button type="button" onClick={switchMode} className="text-secondary-500 hover:text-secondary-600 font-semibold">
                      Sign Up
                    </button>
                  </>
                ) : (
                  <>
                    Already have an account?{' '}
                    <button type="button" onClick={switchMode} className="text-secondary-500 hover:text-secondary-600 font-semibold">
                      Sign In
                    </button>
                  </>
                )}
              </p>
            </form>
          )}

          {/* Verify OTP Form (after signup) */}
          {mode === 'verify-otp' && (
            <form onSubmit={handleOtpSubmit} className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center">
                  <KeyRound className="w-8 h-8 text-secondary-500" />
                </div>
              </div>

              <p className="text-center text-gray-600 text-sm mb-6">
                Enter the 6-digit verification code we sent to your email.
              </p>

              {/* OTP Input Boxes */}
              <div className="flex justify-center gap-1.5 sm:gap-2 mb-6" onPaste={handleOtpPaste}>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (otpRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    disabled={isVerifying}
                    className={`w-10 h-12 sm:w-12 sm:h-14 text-center text-lg sm:text-xl font-bold border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all disabled:bg-gray-50 ${
                      digit ? 'border-primary-500 bg-primary-50' : 'border-gray-200'
                    }`}
                  />
                ))}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isVerifying}
                className="w-full bg-secondary-500 hover:bg-secondary-600 disabled:bg-secondary-300 text-white font-semibold py-3 rounded-lg transition-colors shadow-md hover:shadow-lg flex items-center justify-center gap-2"
              >
                {isVerifying ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify Email'
                )}
              </button>

              {/* Resend OTP */}
              <p className="text-center text-sm text-gray-600 mt-6">
                Didn't receive the code?{' '}
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={isResending}
                  className="text-primary-500 hover:text-primary-600 font-semibold disabled:opacity-50"
                >
                  {isResending ? 'Sending...' : 'Resend'}
                </button>
              </p>
            </form>
          )}

          {/* Forgot Password - Email Step */}
          {mode === 'forgot-email' && (
            <form onSubmit={handleForgotEmailSubmit} className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                  <Mail className="w-8 h-8 text-primary-500" />
                </div>
              </div>

              <p className="text-center text-gray-600 text-sm mb-6">
                Enter your email address and we'll send you instructions to reset your password.
              </p>

              {/* Email Field */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="Enter your email"
                    disabled={isSendingReset}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all disabled:bg-gray-50 ${
                      errors.forgotEmail ? 'border-red-500' : 'border-gray-200'
                    }`}
                  />
                </div>
                {errors.forgotEmail && (
                  <p className="mt-1 text-sm text-red-500">{errors.forgotEmail}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSendingReset}
                className="w-full bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-semibold py-3 rounded-lg transition-colors shadow-md hover:shadow-lg flex items-center justify-center gap-2"
              >
                {isSendingReset ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Reset Instructions'
                )}
              </button>

              {/* Back to Login */}
              <p className="text-center text-sm text-gray-600 mt-6">
                Remember your password?{' '}
                <button type="button" onClick={goBackToLogin} className="text-secondary-500 hover:text-secondary-600 font-semibold">
                  Sign In
                </button>
              </p>
            </form>
          )}

          {/* Forgot Password - OTP Step */}
          {mode === 'forgot-otp' && (
            <form onSubmit={handleForgotOtpSubmit} className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center">
                  <KeyRound className="w-8 h-8 text-secondary-500" />
                </div>
              </div>

              <p className="text-center text-gray-600 text-sm mb-6">
                Enter the 6-digit code we sent to your email to reset your password.
              </p>

              {/* OTP Input Boxes */}
              <div className="flex justify-center gap-1.5 sm:gap-2 mb-6" onPaste={handleOtpPaste}>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (otpRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    disabled={isVerifyingReset}
                    className={`w-10 h-12 sm:w-12 sm:h-14 text-center text-lg sm:text-xl font-bold border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all disabled:bg-gray-50 ${
                      digit ? 'border-primary-500 bg-primary-50' : 'border-gray-200'
                    }`}
                  />
                ))}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isVerifyingReset}
                className="w-full bg-secondary-500 hover:bg-secondary-600 disabled:bg-secondary-300 text-white font-semibold py-3 rounded-lg transition-colors shadow-md hover:shadow-lg flex items-center justify-center gap-2"
              >
                {isVerifyingReset ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify Code'
                )}
              </button>

              {/* Resend OTP */}
              <p className="text-center text-sm text-gray-600 mt-6">
                Didn't receive the code?{' '}
                <button
                  type="button"
                  onClick={handleResendForgotOtp}
                  disabled={isSendingReset}
                  className="text-primary-500 hover:text-primary-600 font-semibold disabled:opacity-50"
                >
                  {isSendingReset ? 'Sending...' : 'Resend'}
                </button>
              </p>
            </form>
          )}

          {/* Reset Password - New Password Step */}
          {mode === 'reset-password' && (
            <form onSubmit={handleResetPasswordSubmit} className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                  <Lock className="w-8 h-8 text-primary-500" />
                </div>
              </div>

              <p className="text-center text-gray-600 text-sm mb-6">
                Enter your new password below.
              </p>

              {/* New Password Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Min 8 characters"
                    disabled={isResettingPassword}
                    className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all disabled:bg-gray-50 ${
                      errors.newPassword ? 'border-red-500' : 'border-gray-200'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.newPassword && (
                  <p className="mt-1 text-sm text-red-500">{errors.newPassword}</p>
                )}
              </div>

              {/* Confirm Password Field */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    disabled={isResettingPassword}
                    className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all disabled:bg-gray-50 ${
                      errors.confirmPassword ? 'border-red-500' : 'border-gray-200'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-500">{errors.confirmPassword}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isResettingPassword}
                className="w-full bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-semibold py-3 rounded-lg transition-colors shadow-md hover:shadow-lg flex items-center justify-center gap-2"
              >
                {isResettingPassword ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// Add Google type to window
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: { client_id: string; callback: (response: { credential: string }) => void }) => void;
          renderButton: (element: HTMLElement, config: {
            type?: string;
            theme?: string;
            size?: string;
            text?: string;
            shape?: string;
            width?: number;
          }) => void;
          prompt: () => void;
        };
      };
    };
  }
}
